import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

function WorkshopAnalytics({ workshopId, instructorId }) {
  const { currentUser } = useAuth();
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    averageRating: 0,
    completionRate: 0,
    refundRate: 0,
    popularTimes: [],
    studentFeedback: [],
    revenueByMonth: [],
    bookingConversion: 0
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('3months');

  useEffect(() => {
    if (currentUser) {
      fetchAnalytics();
    }
  }, [currentUser, workshopId, timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/sessions/instructors/${instructorId}/analytics?time_range=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Workshop Analytics</h3>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="1month">Last Month</option>
          <option value="3months">Last 3 Months</option>
          <option value="6months">Last 6 Months</option>
          <option value="1year">Last Year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 border border-gray-200 rounded-lg">
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(analytics.totalRevenue)}
          </div>
          <div className="text-sm text-gray-600">Total Revenue</div>
        </div>
        
        <div className="bg-white p-6 border border-gray-200 rounded-lg">
          <div className="text-2xl font-bold text-gray-900">
            {analytics.totalBookings}
          </div>
          <div className="text-sm text-gray-600">Total Bookings</div>
        </div>
        
        <div className="bg-white p-6 border border-gray-200 rounded-lg">
          <div className="text-2xl font-bold text-gray-900">
            {analytics.averageRating.toFixed(1)}⭐
          </div>
          <div className="text-sm text-gray-600">Average Rating</div>
        </div>
        
        <div className="bg-white p-6 border border-gray-200 rounded-lg">
          <div className="text-2xl font-bold text-gray-900">
            {analytics.completionRate}%
          </div>
          <div className="text-sm text-gray-600">Completion Rate</div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-white p-6 border border-gray-200 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Revenue Trend</h4>
        <div className="h-64 flex items-end justify-between space-x-2">
          {analytics.revenueByMonth.map((month, index) => (
            <div key={index} className="flex-1 bg-blue-100 hover:bg-blue-200 transition-colors">
              <div 
                className="bg-blue-500 rounded-t"
                style={{ 
                  height: `${(month.revenue / Math.max(...analytics.revenueByMonth.map(m => m.revenue))) * 100}%`,
                  minHeight: '8px'
                }}
              ></div>
              <div className="text-xs text-center mt-2 text-gray-600">
                {month.month}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Student Feedback Summary */}
      <div className="bg-white p-6 border border-gray-200 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Recent Feedback</h4>
        <div className="space-y-4">
          {analytics.studentFeedback.slice(0, 3).map((feedback, index) => (
            <div key={index} className="border-l-4 border-blue-500 pl-4">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-sm font-medium text-gray-900">
                  {feedback.studentName}
                </span>
                <span className="text-yellow-400">
                  {'★'.repeat(feedback.rating)}
                </span>
              </div>
              <p className="text-sm text-gray-600 italic">
                "{feedback.comment}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WorkshopAnalytics; 